import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  IconButton,
  Stack,
  Box,
  Slide,
  SlideProps,
  Fade,
  Grow,
  AlertColor
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

export interface NotificationOptions {
  id?: string;
  title?: string;
  message: string;
  type?: AlertColor;
  duration?: number;
  persistent?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  onClose?: () => void;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  showIcon?: boolean;
  transition?: 'slide' | 'fade' | 'grow';
}

interface Notification extends Required<Omit<NotificationOptions, 'action' | 'onClose'>> {
  id: string;
  action?: NotificationOptions['action'];
  onClose?: NotificationOptions['onClose'];
  timestamp: number;
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (options: NotificationOptions) => string;
  hideNotification: (id: string) => void;
  clearAllNotifications: () => void;
  showSuccess: (message: string, options?: Partial<NotificationOptions>) => string;
  showError: (message: string, options?: Partial<NotificationOptions>) => string;
  showWarning: (message: string, options?: Partial<NotificationOptions>) => string;
  showInfo: (message: string, options?: Partial<NotificationOptions>) => string;
  showRetryNotification: (message: string, onRetry: () => void, options?: Partial<NotificationOptions>) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Transition components
const SlideTransition = (props: SlideProps) => <Slide {...props} direction="up" />;
const FadeTransition = (props: any) => <Fade {...props} />;
const GrowTransition = (props: any) => <Grow {...props} />;

const getTransitionComponent = (transition: string) => {
  switch (transition) {
    case 'fade': return FadeTransition;
    case 'grow': return GrowTransition;
    case 'slide':
    default: return SlideTransition;
  }
};

interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 5
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = useCallback(() => {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      if (notification?.onClose) {
        notification.onClose();
      }
      return prev.filter(n => n.id !== id);
    });
  }, []);

  const showNotification = useCallback((options: NotificationOptions): string => {
    const id = options.id || generateId();
    
    const notification: Notification = {
      id,
      title: options.title || '',
      message: options.message,
      type: options.type || 'info',
      duration: options.duration ?? 6000,
      persistent: options.persistent ?? false,
      position: options.position || { vertical: 'bottom', horizontal: 'right' },
      showIcon: options.showIcon ?? true,
      transition: options.transition || 'slide',
      action: options.action,
      onClose: options.onClose,
      timestamp: Date.now()
    };

    setNotifications(prev => {
      // Remove notification with same ID if exists
      const filtered = prev.filter(n => n.id !== id);
      
      // Add new notification at the beginning
      const updated = [notification, ...filtered];
      
      // Keep only max notifications
      return updated.slice(0, maxNotifications);
    });

    // Auto-hide after duration if not persistent
    if (!notification.persistent && notification.duration > 0) {
      setTimeout(() => {
        hideNotification(id);
      }, notification.duration);
    }

    return id;
  }, [generateId, maxNotifications, hideNotification]);


  const clearAllNotifications = useCallback(() => {
    setNotifications(prev => {
      prev.forEach(notification => {
        if (notification.onClose) {
          notification.onClose();
        }
      });
      return [];
    });
  }, []);

  // Convenience methods for different notification types
  const showSuccess = useCallback((message: string, options?: Partial<NotificationOptions>): string => {
    return showNotification({
      message,
      type: 'success',
      duration: 4000,
      ...options
    });
  }, [showNotification]);

  const showError = useCallback((message: string, options?: Partial<NotificationOptions>): string => {
    return showNotification({
      message,
      type: 'error',
      duration: 8000,
      persistent: true,
      ...options
    });
  }, [showNotification]);

  const showWarning = useCallback((message: string, options?: Partial<NotificationOptions>): string => {
    return showNotification({
      message,
      type: 'warning',
      duration: 6000,
      ...options
    });
  }, [showNotification]);

  const showInfo = useCallback((message: string, options?: Partial<NotificationOptions>): string => {
    return showNotification({
      message,
      type: 'info',
      duration: 5000,
      ...options
    });
  }, [showNotification]);

  const showRetryNotification = useCallback((
    message: string, 
    onRetry: () => void, 
    options?: Partial<NotificationOptions>
  ): string => {
    return showNotification({
      message,
      type: 'error',
      persistent: true,
      action: {
        label: 'Retry',
        onClick: onRetry
      },
      ...options
    });
  }, [showNotification]);

  const contextValue: NotificationContextType = {
    notifications,
    showNotification,
    hideNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showRetryNotification
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

const NotificationContainer: React.FC = () => {
  const context = useContext(NotificationContext);
  if (!context) return null;

  const { notifications, hideNotification } = context;

  // Group notifications by position
  const notificationsByPosition = notifications.reduce((acc, notification) => {
    const key = `${notification.position.vertical}-${notification.position.horizontal}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(notification);
    return acc;
  }, {} as Record<string, Notification[]>);

  return (
    <>
      {Object.entries(notificationsByPosition).map(([positionKey, positionNotifications]) => {
        const [vertical, horizontal] = positionKey.split('-') as ['top' | 'bottom', 'left' | 'center' | 'right'];
        
        return (
          <Stack
            key={positionKey}
            spacing={1}
            sx={{
              position: 'fixed',
              zIndex: 9999,
              [vertical]: 16,
              [horizontal === 'center' ? 'left' : horizontal]: horizontal === 'center' ? '50%' : 16,
              transform: horizontal === 'center' ? 'translateX(-50%)' : 'none',
              maxWidth: '400px',
              minWidth: '300px'
            }}
          >
            {positionNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={() => hideNotification(notification.id)}
              />
            ))}
          </Stack>
        );
      })}
    </>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const TransitionComponent = getTransitionComponent(notification.transition);
  
  const getIcon = (type: AlertColor) => {
    switch (type) {
      case 'success': return <SuccessIcon fontSize="inherit" />;
      case 'error': return <ErrorIcon fontSize="inherit" />;
      case 'warning': return <WarningIcon fontSize="inherit" />;
      case 'info':
      default: return <InfoIcon fontSize="inherit" />;
    }
  };

  return (
    <TransitionComponent in timeout={300}>
      <Alert
        severity={notification.type}
        variant="filled"
        icon={notification.showIcon ? getIcon(notification.type) : false}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {notification.action && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  notification.action!.onClick();
                  onClose();
                }}
                startIcon={notification.action.label.toLowerCase().includes('retry') ? <RefreshIcon /> : undefined}
                sx={{ 
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                {notification.action.label}
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={onClose}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          maxWidth: '100%',
          boxShadow: 3,
          '& .MuiAlert-message': {
            width: '100%',
            overflow: 'hidden'
          }
        }}
      >
        {notification.title && (
          <AlertTitle sx={{ mb: notification.message ? 0.5 : 0 }}>
            {notification.title}
          </AlertTitle>
        )}
        {notification.message && (
          <Box
            sx={{
              wordBreak: 'break-word',
              '& a': {
                color: 'inherit',
                textDecoration: 'underline'
              }
            }}
            dangerouslySetInnerHTML={{ __html: notification.message }}
          />
        )}
      </Alert>
    </TransitionComponent>
  );
};

// Hook for using notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Higher-order component for adding notification support
export const withNotifications = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const WrappedComponent = (props: P) => (
    <NotificationProvider>
      <Component {...props} />
    </NotificationProvider>
  );

  WrappedComponent.displayName = `withNotifications(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default NotificationProvider;
